/**
 * Kalender-Termine: PocketBase. Kein localStorage als Wahrheit.
 * Klasse KALENDER_TILE_SEAT_OR_SKIP.
 */

import { getPortalPocketBaseUrl, getPortalPbToken } from '../../../constants';
import {
  PORTAL_KALENDER_COLLECTION,
  assertCalendarEventWriteAllowed,
  buildKalenderPbListFilter,
  calendarEventVisibleToSeat,
  decodeAttendeeIds,
  encodeAttendeeIds,
  isValidCalendarStoredTime,
  kalenderFilterIsKanzleiWide,
  parseIsoDateLocal,
  sanitizeKalenderSeatId,
  sliceIsoDay,
  type CalendarWriteContext,
  type PortalCalendarEvent,
} from './portalCalendar';

type PbListResponse = {
  items?: Array<Record<string, unknown>>;
};

function pbBase(): string {
  return getPortalPocketBaseUrl().replace(/\/$/, '');
}

function authHeaders(token?: string | null): HeadersInit {
  const resolved = String(token || getPortalPbToken() || '').trim();
  if (!resolved) {
    throw new Error('Keine Session — bitte neu anmelden.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: resolved.startsWith('Bearer ') ? resolved : `Bearer ${resolved}`,
  };
}

async function readError(res: Response): Promise<string> {
  let detail = `HTTP ${res.status}`;
  try {
    const data = (await res.json()) as { message?: unknown };
    if (typeof data.message === 'string' && data.message.trim()) {
      detail = data.message.trim().slice(0, 180);
    }
  } catch {
    /* ignore */
  }
  return detail;
}

export function mapKalenderPbRecord(raw: Record<string, unknown>): PortalCalendarEvent | null {
  const id = typeof raw.id === 'string' ? raw.id.trim() : '';
  const title = typeof raw.title === 'string' ? raw.title.trim() : '';
  const date = sliceIsoDay(typeof raw.date === 'string' ? raw.date : '');
  if (!id || !title || !parseIsoDateLocal(date)) return null;
  const timeRaw = typeof raw.time === 'string' ? raw.time.trim() : '';
  const event: PortalCalendarEvent = {
    id,
    title,
    date,
    customerId: sanitizeKalenderSeatId(typeof raw.customer_id === 'string' ? raw.customer_id : ''),
    kanzleiId: sanitizeKalenderSeatId(typeof raw.kanzlei_id === 'string' ? raw.kanzlei_id : ''),
    attendeeIds: decodeAttendeeIds(typeof raw.attendee_ids === 'string' ? raw.attendee_ids : ''),
    createdAt: typeof raw.created === 'string' ? raw.created : new Date().toISOString(),
  };
  if (timeRaw && isValidCalendarStoredTime(timeRaw)) event.time = timeRaw;
  if (typeof raw.akte_id === 'string' && raw.akte_id.trim()) event.akteId = raw.akte_id.trim();
  if (typeof raw.akte_title === 'string' && raw.akte_title.trim()) {
    event.akteTitle = raw.akte_title.trim();
  }
  return event;
}

export async function loadCalendarEventsFromPb(args: {
  customerId: string;
  kanzleiId: string;
  authToken?: string | null;
}): Promise<PortalCalendarEvent[]> {
  const customerId = sanitizeKalenderSeatId(args.customerId);
  const kanzleiId = sanitizeKalenderSeatId(args.kanzleiId);
  const filter = buildKalenderPbListFilter(customerId, kanzleiId);
  if (!customerId || !kanzleiId || !filter || kalenderFilterIsKanzleiWide(filter)) {
    throw new Error('KALENDER_TILE_SEAT_OR_SKIP: kein Sitz/Kanzlei — Lesen abgebrochen');
  }
  const url =
    `${pbBase()}/api/collections/${PORTAL_KALENDER_COLLECTION}/records` +
    `?filter=${encodeURIComponent(filter)}&perPage=200&sort=date`;
  const res = await fetch(url, { headers: authHeaders(args.authToken) });
  if (res.status === 404) {
    throw new Error('Kalender-Speicher ist auf diesem System noch nicht eingerichtet.');
  }
  if (!res.ok) {
    throw new Error(`Termine konnten nicht geladen werden (${await readError(res)}).`);
  }
  const data = (await res.json()) as PbListResponse;
  return (data.items || [])
    .map((row) => mapKalenderPbRecord(row))
    .filter((row): row is PortalCalendarEvent => row != null)
    .filter((row) => calendarEventVisibleToSeat(row, customerId, kanzleiId));
}

export interface SaveCalendarEventPbInput {
  id?: string;
  title: string;
  date: string;
  time?: string;
  akteId?: string;
  akteTitle?: string;
  attendeeIds?: string[];
}

export async function saveCalendarEventToPb(args: {
  input: SaveCalendarEventPbInput;
  ctx: CalendarWriteContext;
  authToken?: string | null;
}): Promise<PortalCalendarEvent> {
  const customerId = sanitizeKalenderSeatId(args.ctx.customerId);
  const kanzleiId = sanitizeKalenderSeatId(args.ctx.kanzleiId);
  assertCalendarEventWriteAllowed(
    {
      customerId,
      kanzleiId,
      akteId: args.input.akteId,
      attendeeIds: args.input.attendeeIds,
    },
    args.ctx,
  );
  const title = String(args.input.title || '').trim();
  if (!title) throw new Error('Titel fehlt.');
  const date = sliceIsoDay(args.input.date);
  if (!parseIsoDateLocal(date)) throw new Error('Ungültiges Datum.');
  const timeRaw = String(args.input.time || '').trim();
  if (timeRaw && !isValidCalendarStoredTime(timeRaw)) throw new Error('Ungültige Uhrzeit.');

  const payload: Record<string, string> = {
    title,
    date,
    time: timeRaw,
    customer_id: customerId,
    kanzlei_id: kanzleiId,
    attendee_ids: encodeAttendeeIds(args.input.attendeeIds || [], customerId),
    akte_id: String(args.input.akteId || '').trim(),
    akte_title: String(args.input.akteTitle || '').trim(),
  };

  const existingId = String(args.input.id || '').trim();
  const url = existingId
    ? `${pbBase()}/api/collections/${PORTAL_KALENDER_COLLECTION}/records/${encodeURIComponent(existingId)}`
    : `${pbBase()}/api/collections/${PORTAL_KALENDER_COLLECTION}/records`;
  const res = await fetch(url, {
    method: existingId ? 'PATCH' : 'POST',
    headers: authHeaders(args.authToken),
    body: JSON.stringify(payload),
  });
  if (res.status === 404) {
    throw new Error('Kalender-Speicher ist auf diesem System noch nicht eingerichtet.');
  }
  if (!res.ok) {
    throw new Error(`Termin konnte nicht gespeichert werden (${await readError(res)}).`);
  }
  const mapped = mapKalenderPbRecord((await res.json()) as Record<string, unknown>);
  if (!mapped?.id || !calendarEventVisibleToSeat(mapped, customerId, kanzleiId)) {
    throw new Error('KALENDER_TILE_SEAT_OR_SKIP: Speichern ohne eigenen Sitz verworfen');
  }
  if (sanitizeKalenderSeatId(mapped.customerId) !== customerId) {
    throw new Error('KALENDER_TILE_SEAT_OR_SKIP: Speichern ohne eigenen Sitz verworfen');
  }
  return mapped;
}

export async function deleteCalendarEventFromPb(args: {
  event: Pick<PortalCalendarEvent, 'id' | 'customerId' | 'kanzleiId'>;
  ctx: CalendarWriteContext;
  authToken?: string | null;
}): Promise<void> {
  const customerId = sanitizeKalenderSeatId(args.ctx.customerId);
  assertCalendarEventWriteAllowed(
    {
      customerId: args.event.customerId || customerId,
      kanzleiId: args.event.kanzleiId || args.ctx.kanzleiId,
    },
    args.ctx,
  );
  if (sanitizeKalenderSeatId(args.event.customerId) !== customerId) {
    throw new Error('KALENDER_TILE_SEAT_OR_SKIP: fremdes Ereignis — Löschen verworfen');
  }
  const id = String(args.event.id || '').trim();
  if (!id) throw new Error('Termin-ID fehlt.');
  const res = await fetch(
    `${pbBase()}/api/collections/${PORTAL_KALENDER_COLLECTION}/records/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers: authHeaders(args.authToken) },
  );
  if (res.status === 404) return;
  if (!res.ok) {
    throw new Error(`Termin konnte nicht gelöscht werden (${await readError(res)}).`);
  }
}
